import { getImageProps } from 'next/image';

/**
 * Pre-rasterize a remote image into a data URL so the PDF capture can embed it.
 *
 * The asset CDN (i.onefp.net / *.i-dev.onefp.net) serves logos without CORS headers, so a
 * cross-origin <img> can never be drawn to a readable canvas — and html-to-image's fetch()
 * inlining is blocked by the CSP's `connect-src` anyway. Routing the image through the Next
 * image optimizer (/_next/image) makes the request same-origin, where neither restriction
 * applies; the CDN hosts are already in next.config's `images.remotePatterns`. A direct
 * cross-origin load is kept as a fallback for hosts the optimizer rejects. Resolves to null
 * when nothing works (offline, disallowed host) so callers can degrade gracefully.
 */
const loadImageAsDataUrl = async (url: string): Promise<string | null> => {
  const optimized = optimizedSrc(url);
  const fromOptimizer = optimized ? await rasterize(optimized) : null;
  return fromOptimizer ?? rasterize(url);
};

// Default quality only — Next 16 rejects q values outside the configured allowlist.
const optimizedSrc = (url: string): string | null => {
  try {
    return getImageProps({ src: url, alt: '', width: 512, height: 512 }).props.src;
  } catch {
    return null;
  }
};

const rasterize = async (src: string): Promise<string | null> => {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
};

export { loadImageAsDataUrl };
